import { useState, useRef, useEffect, useCallback } from "react";
import { Mic, MicOff, Play, Pause, RotateCcw, Trash2, Check, ChevronLeft, ChevronRight, X, Loader2, ScrollText, HelpCircle, Laptop, Monitor, Smartphone, Tablet } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const WINDOW = 20; // seconds allotted per step (~4s buffer over the 16s narration target)

// Device-specific "how to enable your microphone" guides (browsers own the permission —
// we can only guide users to the right menu chain, we can't flip it for them).
const MIC_HELP = {
  mac: {
    label: "Mac",
    icon: Laptop,
    groups: [
      { title: "Safari", steps: ["Click the “aA” / page-settings icon in the address bar", "Choose Website Settings", "Set Microphone to Allow", "Reload the page"] },
      { title: "Chrome / Edge", steps: ["Click the 🔒 lock icon in the address bar", "Open Site settings", "Set Microphone to Allow", "Reload the page"] },
      { title: "Still blocked? Check macOS", steps: ["Apple menu  →  System Settings", "Privacy & Security  →  Microphone", "Turn ON your browser (Safari / Chrome)"] },
    ],
  },
  pc: {
    label: "Windows PC",
    icon: Monitor,
    groups: [
      { title: "Chrome / Edge", steps: ["Click the 🔒 lock icon in the address bar", "Open Site permissions / Permissions for this site", "Set Microphone to Allow", "Reload the page"] },
      { title: "Still blocked? Check Windows", steps: ["Settings  →  Privacy & security", "Microphone", "Turn on “Let apps access your microphone”", "Enable your browser in the list"] },
    ],
  },
  iphone: {
    label: "iPhone",
    icon: Smartphone,
    groups: [
      { title: "Safari (per-site)", steps: ["Tap “aA” on the left of the address bar", "Tap Website Settings", "Set Microphone to Allow", "Reload the page"] },
      { title: "Still blocked? Check iOS", steps: ["Settings  →  Safari  →  Microphone  →  Allow", "or Settings  →  Privacy & Security  →  Microphone", "Turn ON Safari / Chrome"] },
    ],
  },
  android: {
    label: "Android",
    icon: Tablet,
    groups: [
      { title: "Chrome", steps: ["Tap the 🔒 lock icon in the address bar", "Tap Permissions", "Set Microphone to Allow", "Reload the page"] },
      { title: "Still blocked? Check Android", steps: ["Settings  →  Apps  →  Chrome", "Permissions  →  Microphone", "Choose Allow"] },
    ],
  },
};

export default function VoiceStudioModal({ manual, onClose, onSaved, showLabels = true, inline = false }) {
  const steps = manual?.steps || [];
  const [voMode, setVoMode] = useState(manual?.vo_mode === "block" ? "block" : "per_step");
  const [voBlocks, setVoBlocks] = useState(manual?.vo_blocks || []);
  const blockMode = voMode === "block";
  const blocks = voBlocks;
  const units = blockMode ? blocks : steps;
  const [idx, setIdx] = useState(0);
  const [recorded, setRecorded] = useState(() => steps.map((s) => !!s.user_voice_id));
  const [phase, setPhase] = useState("idle"); // idle | recording | saving
  const [remaining, setRemaining] = useState(WINDOW);
  const [playing, setPlaying] = useState(false);
  const [showScript, setShowScript] = useState(true);
  const [showMicHelp, setShowMicHelp] = useState(false);
  const [helpDevice, setHelpDevice] = useState(null); // null = device picker; else key of MIC_HELP

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const localUrlRef = useRef(null);
  const audioElRef = useRef(null);
  const sfxCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const [level, setLevel] = useState(0); // 0..1 live mic level for the VU meter
  const [clip, setClip] = useState(false);
  const [muted, setMuted] = useState(false);
  const [playProgress, setPlayProgress] = useState(0); // 0..1 while previewing a take
  const [wavePeaks, setWavePeaks] = useState(null);    // decoded waveform of the current take

  const _ctx = useCallback(() => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!sfxCtxRef.current) sfxCtxRef.current = new AC();
    if (sfxCtxRef.current.state === "suspended") sfxCtxRef.current.resume();
    return sfxCtxRef.current;
  }, []);

  // Short metallic "ting" — fundamental + inharmonic partial with a fast decay.
  const ting = useCallback((freq, dur = 0.13, gainPeak = 0.22) => {
    try {
      const ctx = _ctx();
      if (!ctx) return;
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(gainPeak, now + 0.006);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
      gain.connect(ctx.destination);
      [[freq, "triangle", 1], [freq * 2.76, "sine", 0.4]].forEach(([f, type, mul]) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        g.gain.value = mul;
        o.type = type;
        o.frequency.setValueAtTime(f, now);
        o.connect(g);
        g.connect(gain);
        o.start(now);
        o.stop(now + dur + 0.02);
      });
    } catch { /* noop */ }
  }, [_ctx]);

  const sfxStart = useCallback(() => ting(1046, 0.16, 0.24), [ting]); // record on — bright
  const sfxStop = useCallback(() => ting(523, 0.2, 0.24), [ting]);    // record off — lower
  const sfxRedo = useCallback(() => ting(415, 0.16, 0.26), [ting]);   // redo / delete take
  const sfxPlay = useCallback(() => ting(880, 0.12, 0.2), [ting]);    // play
  const sfxNext = useCallback(() => ting(1245, 0.1, 0.2), [ting]);    // next — higher
  const sfxBack = useCallback(() => ting(740, 0.1, 0.2), [ting]);     // back — lower

  const stopVU = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    analyserRef.current = null;
    setLevel(0);
    setClip(false);
  }, []);

  // Live VU meter — continuously reads peak level off the mic (not routed to output, so no feedback).
  const startMeter = useCallback((stream) => {
    try {
      const ctx = _ctx();
      if (!ctx) return;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.fftSize);
      const loop = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buf);
        let peak = 0;
        for (let i = 0; i < buf.length; i++) { const v = Math.abs(buf[i] - 128) / 128; if (v > peak) peak = v; }
        setLevel(peak);
        setClip(peak > 0.92);
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch { /* meter is optional */ }
  }, [_ctx]);

  // Grab the mic once and keep it hot so the level meter is live even before recording.
  const ensureMic = useCallback(async () => {
    if (streamRef.current) { try { _ctx(); } catch { /* noop */ } return streamRef.current; }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    stream.getAudioTracks().forEach((t) => { t.enabled = !muted; });
    startMeter(stream);
    return stream;
  }, [startMeter, _ctx, muted]);

  // Mute / unmute the mic input (affects both the live meter and any recording).
  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (streamRef.current) streamRef.current.getAudioTracks().forEach((t) => { t.enabled = !next; });
      if (next) { setLevel(0); setClip(false); }
      return next;
    });
  }, []);

  const step = steps[idx] || {};
  const total = units.length;
  const curWin = blockMode ? Math.min(240, Math.max(30, ((blocks[idx]?.end - blocks[idx]?.start + 1) || 1) * 45)) : WINDOW;
  const isRec = (i) => (blockMode ? !!blocks[i]?.audio_id : !!recorded[i]);
  const curVoiceId = blockMode ? blocks[idx]?.audio_id : step.user_voice_id;
  const script = blockMode
    ? (blocks[idx]
        ? steps.slice(blocks[idx].start, blocks[idx].end + 1)
            .map((s, n) => `${blocks[idx].start + n + 1}. ${(s.narration || s.title || "").trim()}`)
            .join("\n\n")
        : "")
    : (step.narration || step.title || "").trim();
  const takeOptions = [1, 3, 5, 8].filter((v) => v <= Math.max(1, steps.length));
  if (takeOptions.length === 0) takeOptions.push(1);
  const applyVoConfig = async (mode, count) => {
    try {
      const r = await api.post(`/manuals/${manual.id}/vo/config`, { mode, count });
      setVoMode(r.data?.mode === "block" ? "block" : "per_step");
      setVoBlocks(r.data?.blocks || []);
      setIdx(0);
      stopPreview();
      onSaved?.();
      toast.success(mode === "block" ? `Block VO · ${count} take${count > 1 ? "s" : ""} 🎙️` : "Per-step voiceover");
    } catch {
      toast.error("Couldn't switch voice mode — please try again.");
    }
  };

  const stopTracks = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };
  const clearTick = () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };

  const stopPreview = useCallback(() => {
    if (audioElRef.current) { try { audioElRef.current.pause(); } catch { /* noop */ } audioElRef.current = null; }
    setPlaying(false);
    setPlayProgress(0);
  }, []);

  useEffect(() => {
    // reset per-step preview when navigating
    stopPreview();
    if (localUrlRef.current) { URL.revokeObjectURL(localUrlRef.current); localUrlRef.current = null; }
    setRemaining(curWin);
    setPhase("idle");
  }, [idx, stopPreview]);

  useEffect(() => () => { clearTick(); stopTracks(); stopPreview(); stopVU(); }, [stopPreview, stopVU]);

  // Start the live input-level meter as soon as the studio opens (mic prompt appears here).
  useEffect(() => {
    let cancelled = false;
    (async () => { try { if (!cancelled) await ensureMic(); } catch { /* denied — meter stays dark */ } })();
    return () => { cancelled = true; };
  }, [ensureMic]);

  const uploadClip = async (blob) => {
    setPhase("saving");
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(blob);
      });
      if (blockMode) {
        const r = await api.post(`/manuals/${manual.id}/vo/blocks/${idx}/voiceover`, { audio: dataUrl });
        setVoBlocks((prev) => prev.map((b, j) => (j === idx ? { ...b, audio_id: r.data?.audio_id || "saved" } : b)));
      } else {
        await api.post(`/manuals/${manual.id}/steps/${idx}/voiceover`, { audio: dataUrl });
        setRecorded((prev) => { const n = [...prev]; n[idx] = true; return n; });
      }
      if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
      localUrlRef.current = URL.createObjectURL(blob);
      toast.success(blockMode ? `Take ${idx + 1} saved 🎙️` : `Step ${idx + 1} voiceover saved 🎙️`);
      onSaved?.();
    } catch {
      toast.error("Couldn't save that take — try again.");
    } finally {
      setPhase("idle");
    }
  };

  const startRecording = async () => {
    stopPreview();
    try {
      const stream = await ensureMic();
      // Always capture audio — recording forces the input on even if the user had muted the monitor.
      stream.getAudioTracks().forEach((t) => { t.enabled = true; });
      if (muted) setMuted(false);
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        clearTick();
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size > 0) uploadClip(blob);
      };
      mediaRef.current = mr;
      mr.start();
      sfxStart();
      setPhase("recording");
      setRemaining(curWin);
      const startedAt = Date.now();
      tickRef.current = setInterval(() => {
        const left = curWin - (Date.now() - startedAt) / 1000;
        if (left <= 0) { setRemaining(0); stopRecording(); }
        else setRemaining(left);
      }, 100);
    } catch {
      toast.error("Microphone access denied. Allow mic access to record.");
      setHelpDevice(null); setShowMicHelp(true);
    }
  };

  const stopRecording = () => {
    sfxStop();
    clearTick();
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    setPhase("saving");
  };

  const playBack = () => {
    if (playing) { stopPreview(); return; }
    sfxPlay();
    const url = localUrlRef.current || `${process.env.REACT_APP_BACKEND_URL}/api/audio/${curVoiceId}`;
    if (!localUrlRef.current && !curVoiceId) return;
    const a = new Audio(url);
    audioElRef.current = a;
    setPlaying(true);
    setPlayProgress(0);
    a.ontimeupdate = () => { if (a.duration) setPlayProgress(Math.min(1, a.currentTime / a.duration)); };
    a.onended = () => { setPlaying(false); setPlayProgress(0); };
    a.play().catch(() => setPlaying(false));
  };

  const deleteClip = async () => {
    sfxRedo();
    stopPreview();
    try {
      if (blockMode) {
        await api.delete(`/manuals/${manual.id}/vo/blocks/${idx}/voiceover`);
        setVoBlocks((prev) => prev.map((b, j) => (j === idx ? { ...b, audio_id: null } : b)));
      } else {
        await api.delete(`/manuals/${manual.id}/steps/${idx}/voiceover`);
        setRecorded((prev) => { const n = [...prev]; n[idx] = false; return n; });
      }
      if (localUrlRef.current) { URL.revokeObjectURL(localUrlRef.current); localUrlRef.current = null; }
      toast.message(blockMode ? `Take ${idx + 1} removed` : `Step ${idx + 1} voiceover removed`);
      onSaved?.();
    } catch { /* noop */ }
  };

  const hasClip = isRec(idx) || !!localUrlRef.current;
  const pct = phase === "recording" ? Math.max(0, (remaining / curWin) * 100) : 100;

  // Horizontal VU meter — segmented LED look (green → yellow → red).
  const SEG_COUNT = 16;
  const litSegs = Math.min(SEG_COUNT, Math.round(level * SEG_COUNT * 1.15));
  const segColor = (i) => (i >= 13 ? "#ef4444" : i >= 10 ? "#eab308" : "#22c55e");
  const labelCls = "text-[10px] font-bold leading-none";
  // Live input readout (peak dBFS) shown next to the meter.
  const db = level > 0.0015 ? Math.round(20 * Math.log10(level)) : null;
  const levelReadout = clip ? "CLIP" : db === null ? "−∞" : `${db} dB`;
  const BAR_COUNT = 44;
  const fallbackPeaks = Array.from({ length: BAR_COUNT }, (_, i) => 0.35 + 0.5 * Math.abs(Math.sin(i * 0.6)));

  // Decode the current take into a little waveform for the preview bar (best-effort eye candy).
  useEffect(() => {
    let cancelled = false;
    setWavePeaks(null);
    if (!hasClip) return;
    const url = localUrlRef.current || `${process.env.REACT_APP_BACKEND_URL}/api/audio/${curVoiceId}`;
    (async () => {
      try {
        const ctx = _ctx(); if (!ctx) return;
        const resp = await fetch(url);
        const arr = await resp.arrayBuffer();
        const audioBuf = await ctx.decodeAudioData(arr);
        const ch = audioBuf.getChannelData(0);
        const block = Math.max(1, Math.floor(ch.length / BAR_COUNT));
        const peaks = [];
        for (let b = 0; b < BAR_COUNT; b++) {
          let max = 0; const start = b * block;
          for (let j = 0; j < block; j++) { const v = Math.abs(ch[start + j] || 0); if (v > max) max = v; }
          peaks.push(max);
        }
        const norm = Math.max(...peaks, 0.001);
        if (!cancelled) setWavePeaks(peaks.map((p) => Math.max(0.1, p / norm)));
      } catch { if (!cancelled) setWavePeaks(null); }
    })();
    return () => { cancelled = true; };
  }, [hasClip, curVoiceId, idx, _ctx]);

  return (
    <div className={inline ? "w-full" : "fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-3 sm:p-4"} data-testid="voice-studio-modal">
      <div className={inline
        ? "relative w-full max-w-[700px] mx-auto rounded-[24px] overflow-hidden ring-1 ring-black/10 shadow-xl bg-[#0b0b0e] flex flex-col"
        : "relative w-full max-w-[520px] max-h-[94vh] overflow-y-auto rounded-[26px] shadow-2xl ring-1 ring-white/15 bg-[#0b0b0e] flex flex-col"}
        onPointerDown={() => { try { _ctx(); } catch { /* noop */ } }}>

        {/* ===== TOP CONSOLE (own zone — never overlaps the mic) ===== */}
        <div className="shrink-0 px-4 pt-3 pb-3.5 bg-[#0c0c11] border-b border-white/10">
          {/* Mic input-level meter (full width) + Mute + close */}
          <div className="flex items-center gap-2">
            <div data-testid="studio-vu-meter"
              className="flex-1 h-8 rounded-lg bg-black/70 flex items-center gap-[2px] px-1.5 overflow-hidden transition-shadow"
              style={{ border: (phase === "recording" || clip) ? "1px solid rgba(239,68,68,0.9)" : "1px solid rgba(255,255,255,0.14)", boxShadow: clip ? "0 0 10px 1px rgba(239,68,68,0.6)" : "none" }}
              title="Microphone input level">
              {Array.from({ length: SEG_COUNT }).map((_, i) => {
                const on = i < litSegs;
                return (
                  <span key={i} className="flex-1 h-4 rounded-[1px] transition-all duration-75"
                    style={{ background: on ? segColor(i) : "rgba(255,255,255,0.07)", boxShadow: on ? `0 0 4px ${segColor(i)}` : "none" }} />
                );
              })}
            </div>
            <span data-testid="studio-level-readout"
              className="shrink-0 w-14 text-center font-mono font-bold text-[12px] tabular-nums transition-colors"
              style={{ color: clip ? "#ef4444" : level > 0.0015 ? "#f0c975" : "rgba(255,255,255,0.4)" }}
              title="Live input level (peak)">
              {levelReadout}
            </span>
            <button onClick={toggleMute} data-testid="studio-mute"
              aria-pressed={muted}
              className={`shrink-0 flex items-center gap-1.5 rounded-lg px-3 h-8 text-[12px] font-bold transition-colors ${
                muted ? "bg-[#ef4444] text-white" : "bg-white/10 text-white/85 hover:bg-white/20 border border-white/20"}`}>
              {muted ? <MicOff size={15} /> : <Mic size={15} />} Mute
            </button>
            {!inline && (
              <button data-testid="studio-close" onClick={onClose}
                className="p-1.5 rounded-full bg-white/10 text-white/90 hover:bg-white/20 transition-colors shrink-0"><X size={18} /></button>
            )}
          </div>

          {/* Voice mode: many concise per-step takes, or fewer flowing Block takes */}
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-testid="vo-mode-row">
            <span className="text-[11px] font-bold text-white/55">Voice&nbsp;mode:</span>
            <div className="inline-flex rounded-full bg-black/50 border border-white/15 p-0.5">
              <button data-testid="vo-mode-perstep" onClick={() => applyVoConfig("per_step", 1)}
                className={`px-3 h-7 rounded-full text-[11px] font-bold transition-colors ${!blockMode ? "bg-[#f0c975] text-black" : "text-white/70 hover:text-white"}`}>Per-step</button>
              <button data-testid="vo-mode-block" onClick={() => applyVoConfig("block", takeOptions.includes(3) ? 3 : takeOptions[takeOptions.length - 1])}
                className={`px-3 h-7 rounded-full text-[11px] font-bold transition-colors ${blockMode ? "bg-[#f0c975] text-black" : "text-white/70 hover:text-white"}`}>Free / Block VO</button>
            </div>
            {blockMode && (
              <div className="inline-flex items-center gap-1" data-testid="vo-takes-row">
                <span className="text-[11px] font-bold text-white/55 ml-1">Takes:</span>
                {takeOptions.map((v) => (
                  <button key={v} data-testid={`vo-takes-${v}`} onClick={() => applyVoConfig("block", v)}
                    className={`w-7 h-7 rounded-full text-[11px] font-bold transition-colors ${blocks.length === v ? "bg-[#f0c975] text-black" : "bg-white/10 text-white/70 border border-white/20 hover:bg-white/20"}`}>{v}</button>
                ))}
              </div>
            )}
          </div>
          {blockMode && (
            <p className="text-center text-[10px] text-white/50 leading-snug mt-1" data-testid="vo-block-hint">
              One flowing take per group — perform naturally; the pictures follow your words automatically.
            </p>
          )}

          {/* Step / Block selector — wraps to multiple rows so it never bleeds off the card */}
          <div className="flex flex-wrap justify-center gap-2 mt-3">
            {units.map((u, i) => (
              <button key={i} onClick={() => setIdx(i)} data-testid={`studio-dot-${i}`}
                title={blockMode ? `Take ${i + 1} · steps ${u.start + 1}–${u.end + 1}` : `Step ${i + 1}`}
                className={`shrink-0 h-7 rounded-full text-[11px] font-bold flex items-center justify-center px-2 min-w-7 transition-all ${
                  i === idx ? "ring-2 ring-[#f0c975] ring-offset-1 ring-offset-[#0c0c11] " : ""}${
                  isRec(i) ? "bg-[#f0c975] text-black" : "bg-white/10 text-white/80 border border-white/20"}`}>
                {isRec(i) ? <Check size={13} /> : blockMode ? `${u.start + 1}–${u.end + 1}` : i + 1}
              </button>
            ))}
          </div>

          {/* Teleprompter */}
          <div className="mt-2.5">
            <button onClick={() => setShowScript((v) => !v)} data-testid="studio-toggle-script"
              className="flex items-center gap-1.5 text-[12px] font-bold text-[#f0c975] mb-1.5">
              <ScrollText size={14} /> {showScript ? "Hide script" : "Show script"} <span className="text-white/50 font-medium">· optional — or freestyle</span>
            </button>
            {showScript && (
              <p className="text-[16px] leading-relaxed text-white/95 font-medium max-h-[30vh] overflow-auto pr-1" data-testid="studio-script">
                {script || "Say whatever you'd like for this step."}
              </p>
            )}
          </div>
        </div>

        {/* ===== STUDIO IMAGE (its own band — the mic is fully visible) ===== */}
        <div className="relative shrink-0 w-full aspect-square max-h-[56vh] overflow-hidden bg-black">
          <img src="/studio-bg-v2.jpg" alt="" aria-hidden
            className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none" />
        </div>

        {/* ===== RECORD DECK (own zone at the bottom — compact) ===== */}
        <div className="shrink-0 px-4 pt-2.5 pb-3 bg-[#0c0c11] border-t border-white/10">
          {/* Countdown + timer */}
          <div className="flex flex-col items-center">
            <div className="w-44 h-1.5 rounded-full bg-black/60 border border-white/10 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-100 ease-linear"
                style={{ width: `${pct}%`, background: phase === "recording" ? "linear-gradient(90deg,#f0c975,#ef4444)" : "rgba(240,201,117,0.5)" }} data-testid="studio-countdown-bar" />
            </div>
            <div className="mt-1 font-mono font-bold text-sm tabular-nums" style={{ color: phase === "recording" ? "#ef4444" : "#f0c975" }} data-testid="studio-timer">
              0:{String(Math.ceil(remaining)).padStart(2, "0")}
            </div>
          </div>

          {/* Your take — appears once this step is recorded, with a waveform + clear preview */}
          {hasClip && phase !== "recording" && phase !== "saving" && (
            <div className="mt-2" data-testid="studio-take-bar">
              {/* Waveform — fills with gold as the take plays */}
              <div className="flex items-center justify-center gap-[2px] h-9 px-2 mb-2 cursor-pointer"
                data-testid="studio-waveform" onClick={playBack} role="button" title={playing ? "Stop" : "Preview take"}>
                {(wavePeaks || fallbackPeaks).map((p, i) => {
                  const active = (i + 0.5) / BAR_COUNT <= playProgress;
                  return (
                    <span key={i} className="w-[3px] rounded-full transition-[height,background-color] duration-150"
                      style={{ height: `${Math.round(18 + p * 82)}%`, background: active ? "#f0c975" : "rgba(240,201,117,0.28)" }} />
                  );
                })}
              </div>
              <div className="flex items-center justify-center flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#f0c975]">
                  <Check size={13} /> Take saved
                </span>
                <button onClick={playBack} data-testid="studio-play"
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 h-8 text-[12px] font-bold bg-[#f0c975] text-black hover:bg-[#e8b74d] active:scale-95 transition-all">
                  {playing ? <Pause size={15} /> : <Play size={15} />} {playing ? "Stop" : "Preview take"}
                </button>
                <button onClick={deleteClip} data-testid="studio-delete" title="Delete this take"
                  className="inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-[12px] font-bold bg-white/10 text-[#f7a0b4] border border-white/20 hover:bg-white/20 active:scale-95 transition-all">
                  <Trash2 size={15} /> Delete
                </button>
              </div>
            </div>
          )}

          {/* Control row: Back · RECORD · Next/Done */}
          <div className="flex items-center justify-between mt-2">
            {/* Back */}
            <div className="flex flex-col items-center gap-1 w-[54px]">
              <button onClick={() => { sfxBack(); setIdx((i) => Math.max(0, i - 1)); }} disabled={idx === 0 || phase === "recording"}
                data-testid="studio-prev" title="Previous step"
                className="w-[50px] h-[50px] rounded-full bg-white/8 border border-white/25 text-white flex items-center justify-center hover:bg-white/16 active:scale-95 transition-all disabled:opacity-25">
                <ChevronLeft size={26} />
              </button>
              {showLabels && <span className={`${labelCls} text-white/80`}>Back</span>}
            </div>

            {/* Record / Stop / Re-record */}
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={phase === "recording" ? stopRecording : startRecording}
                disabled={phase === "saving"}
                data-testid={phase === "recording" ? "studio-stop" : "studio-record"}
                className="relative w-[74px] h-[74px] rounded-full flex items-center justify-center transition-transform active:scale-95 disabled:opacity-70"
                style={{ border: "3px solid rgba(255,255,255,0.92)", boxShadow: phase === "recording" ? "0 0 26px 3px rgba(225,29,47,0.6)" : "0 4px 16px rgba(0,0,0,0.5)" }}>
                <span
                  className="flex items-center justify-center text-white transition-all duration-200"
                  style={phase === "recording"
                    ? { width: 30, height: 30, borderRadius: 9, background: "#e11d2f" }
                    : { width: 58, height: 58, borderRadius: 999, background: "linear-gradient(135deg,#f0505f,#e11d2f)" }}>
                  {phase === "saving" ? <Loader2 size={22} className="animate-spin" />
                    : phase === "recording" ? null
                    : hasClip ? <RotateCcw size={24} /> : <Mic size={24} />}
                </span>
              </button>
              <span className="text-[11px] font-bold text-white/90">
                {phase === "recording" ? "Stop" : phase === "saving" ? "Saving…" : hasClip ? "Re-record" : "Record"}
              </span>
            </div>

            {/* Next / Done */}
            {idx + 1 < total ? (
              <div className="flex flex-col items-center gap-1 w-[54px]">
                <button onClick={() => { sfxNext(); setIdx((i) => Math.min(total - 1, i + 1)); }} disabled={phase === "recording"}
                  data-testid="studio-next" title="Next step"
                  className="w-[50px] h-[50px] rounded-full bg-[#f0c975]/12 border border-[#f0c975]/60 text-[#f0c975] flex items-center justify-center hover:bg-[#f0c975]/22 active:scale-95 transition-all disabled:opacity-25">
                  <ChevronRight size={28} />
                </button>
                {showLabels && <span className={`${labelCls} text-[#f0c975]`}>Next</span>}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 w-[54px]">
                <button onClick={onClose} data-testid="studio-done" title="Done"
                  className="w-[50px] h-[50px] rounded-full bg-[#f0c975] text-black flex items-center justify-center hover:bg-[#e8b74d] active:scale-95 transition-all">
                  <Check size={26} strokeWidth={3} />
                </button>
                {showLabels && <span className={`${labelCls} text-[#f0c975]`}>Done</span>}
              </div>
            )}
          </div>

          {/* Mic help + caption */}
          <div className="mt-2 text-center">
            <button onClick={() => { setHelpDevice(null); setShowMicHelp(true); }} data-testid="studio-mic-help-open"
              className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white/80 hover:text-[#f0c975] transition-colors">
              <HelpCircle size={13} /> How to turn on my mic
            </button>
            <p className="text-[10px] text-white/55 leading-snug mt-1">
              Steps you don't record simply play with music (and your text) — your video, your call.
            </p>
          </div>
        </div>
      </div>

      {/* Mic help popup */}
      {showMicHelp && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowMicHelp(false)} data-testid="mic-help-overlay">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-[#e7defb]"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 bg-[#171426] text-white">
              <div className="flex items-center gap-2 font-extrabold">
                {helpDevice ? (
                  <button onClick={() => setHelpDevice(null)} data-testid="mic-help-back" className="p-1 -ml-1 rounded-full hover:bg-white/10">
                    <ChevronLeft size={18} />
                  </button>
                ) : <Mic size={18} className="text-[#a9c39c]" />}
                {helpDevice ? `Enable mic — ${MIC_HELP[helpDevice].label}` : "Turn on your microphone"}
              </div>
              <button data-testid="mic-help-close" onClick={() => setShowMicHelp(false)} className="p-1 rounded-full hover:bg-white/10"><X size={20} /></button>
            </div>

            <div className="px-5 py-5">
              {!helpDevice ? (
                <>
                  <p className="text-sm text-[#6b6880] mb-4">
                    Your browser controls mic access — pick your device and we'll show you exactly where to allow it.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(MIC_HELP).map(([key, d]) => {
                      const Ico = d.icon;
                      return (
                        <button key={key} onClick={() => setHelpDevice(key)} data-testid={`mic-help-device-${key}`}
                          className="flex flex-col items-center gap-2 rounded-2xl border border-[#e7defb] bg-[#faf8ff] py-5 hover:border-[#4f7746] hover:bg-[#f3edff] transition-colors">
                          <Ico size={26} className="text-[#4f7746]" />
                          <span className="font-bold text-sm text-[#2c2841]">{d.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="space-y-4" data-testid="mic-help-steps">
                  {MIC_HELP[helpDevice].groups.map((g, gi) => (
                    <div key={gi} className="rounded-2xl border border-[#e7defb] bg-[#faf8ff] p-4">
                      <div className="font-extrabold text-sm text-[#4f7746] mb-2">{g.title}</div>
                      <ol className="space-y-2">
                        {g.steps.map((s, si) => (
                          <li key={si} className="flex items-start gap-2.5 text-[14px] text-[#2c2841]">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#4f7746] text-white text-[11px] font-bold flex items-center justify-center mt-0.5">{si + 1}</span>
                            <span className="leading-snug">{s}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                  <p className="text-center text-[11px] text-[#b3aec6]">After allowing, come back and tap Record again.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
